import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowRight, Mail, Info } from "lucide-react";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!form.email) return toast.error("Email is required");
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, plan_label: i.plan_label })),
        guest_email: user ? undefined : form.email,
        guest_name: user ? undefined : form.name,
        guest_phone: form.phone,
      };
      const { data } = await api.post("/checkout/create", payload);
      // Save contact email to localStorage so we can query guest orders later
      const contactEmail = form.email || user?.email || "";
      if (contactEmail) localStorage.setItem("kyro_last_email", contactEmail);
      clear();
      toast.success("Order created — pay via UPI to confirm");
      nav(`/order/${data.order_id}?email=${encodeURIComponent(contactEmail)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display font-black text-4xl tracking-tighter">Your vault is empty</h1>
        <p className="text-kyro-mute mt-3">Add a subscription to continue.</p>
        <button onClick={() => nav("/shop")} className="kyro-btn-primary mt-8">Browse shop</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
      <div className="mb-10">
        <div className="kyro-overline">Checkout</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Your details</h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 space-y-6">
          <div className="kyro-card p-6 md:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Mail className="w-4 h-4 text-[#FF4433]" />
              <span className="kyro-overline">Delivery details</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-kyro-mute mb-2 block">Email {user && "(from account)"}</label>
                <input
                  data-testid="checkout-email"
                  type="email"
                  required
                  disabled={!!user}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-[#FF4433] disabled:opacity-60"
                  placeholder="you@domain.com"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <input data-testid="checkout-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-[#FF4433]" placeholder="Full name" />
                <input data-testid="checkout-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-[#FF4433]" placeholder="Phone (optional)" />
              </div>
              {!user && (
                <p className="text-xs text-kyro-mute flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Checking out as guest. Your order will be trackable with this email + order ID.</p>
              )}
            </div>
          </div>

          <div className="kyro-card p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[#FF4433]" />
              <span className="kyro-overline">Payment</span>
            </div>
            <p className="text-sm text-kyro-mute leading-relaxed">
              After placing your order you'll see a <strong className="text-kyro-ink">UPI QR code</strong>, UPI ID, GPay number and PhonePe number. Pay via any UPI app (GPay, PhonePe, Paytm, BHIM) and then click the <strong className="text-kyro-ink">"I've paid"</strong> button on the next page. We'll verify and deliver within 1–2 hours.
            </p>
            <button data-testid="place-order-btn" type="submit" disabled={loading} className="mt-6 kyro-btn-primary w-full flex items-center justify-center gap-2">
              {loading ? "Creating order..." : <>Place order — ₹{subtotal.toFixed(0)} <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-[11px] text-kyro-faint text-center mt-3">By placing this order you agree to our terms & refund policy.</p>
          </div>
        </form>

        <aside className="lg:col-span-2">
          <div className="kyro-card p-6 md:p-8 sticky top-24">
            <div className="kyro-overline mb-5">Order summary</div>
            <div className="space-y-3 mb-6">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <div className="w-12 h-12 rounded-md bg-white/5 overflow-hidden shrink-0">
                    {i.image_url && <img src={i.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{i.name}</p>
                    <p className="text-xs text-kyro-mute">{i.plan_label}</p>
                  </div>
                  <p className="text-sm font-semibold">₹{i.price.toFixed(0)}</p>
                </div>
              ))}
            </div>
            <div className="kyro-divider mb-4" />
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-kyro-mute">Subtotal</span>
              <span>₹{subtotal.toFixed(0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mb-4">
              <span className="text-kyro-mute">Delivery</span>
              <span className="text-[#FF4433]">Within 1–2 hours • Free</span>
            </div>
            <div className="kyro-divider mb-4" />
            <div className="flex items-center justify-between">
              <span className="text-kyro-mute text-sm">Total</span>
              <span className="font-display text-2xl font-black">₹{subtotal.toFixed(0)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
